<template>
	<div class="list" ref="list">
		<ul :style="ulStyle">
			<li
				v-for="(item, index) in column"
				:key="'item' + index"
				:class="{ disabled: item.disabled }"
			>
				{{ item.label }}
			</li>
		</ul>
	</div>
</template>

<script>
import { getClient, START_EVENT, MOVE_EVENT, END_EVENT, isPC } from './utils.js'
const DEFAULT_DURATION = 200
// 惯性滑动思路:
// 在手指离开屏幕时，如果和上一次 move 时的间隔小于 `LIMIT_TIME` 且 move
// 距离大于 `LIMIT_DISTANCE` 时，执行惯性滑动
const LIMIT_TIME = 300
const LIMIT_DISTANCE = 15
const IS_PC = isPC()
export default {
	props: {
		defaultIndex: {
			type: Number,
			default: 0,
		},
		column: {
			type: Array,
			default: () => [],
		},
		boxHeight: Number,
		itemHeight: Number,
		rowNumber: Number,
	},
	data() {
		return {
			ulStyle: {
				transform: `translate3d(0px, 0px, 0px)`,
				transitionDuration: `0ms`,
				transitionProperty: `none`,
				lineHeight: `${this.itemHeight}px`,
			},
		}
	},
	computed: {
		count() {
			return this.column.length
		},
		getRoNumber() {
			return Math.floor(this.rowNumber / 2)
		},
	},
	methods: {
		findSelectableIndex(preferredIndex = 0, direction = 0) {
			const items = this.column
			if (!items.length) return 0
			const clampedIndex = Math.min(
				Math.max(preferredIndex, 0),
				items.length - 1,
			)
			if (!items[clampedIndex]?.disabled) return clampedIndex

			for (let offset = 1; offset < items.length; offset++) {
				// 由近而遠向兩側找可選項，只有滾動方向決定先試哪一側：
				// 往上滾（負方向）先往前找，其餘情形（含無方向）先往後找
				const tryIndices =
					direction < 0
						? [clampedIndex - offset, clampedIndex + offset]
						: [clampedIndex + offset, clampedIndex - offset]

				for (const idx of tryIndices) {
					if (idx >= 0 && idx < items.length && !items[idx].disabled) return idx
				}
			}
			return clampedIndex
		},
		// 程式化定位，瞬間完成：transition 參數會殘留上一次拖曳 / 慣性的設定
		// （200ms / 1000ms），面板是 v-show、本元件不銷毀，不重設會跑出滑行動畫。
		// 不可併入 setTop —— setTranfromTop 與 toMove 是刻意先設好 transition 才呼叫它
		jumpTo(index) {
			this.ulStyle.transitionDuration = `0ms`
			this.ulStyle.transitionProperty = `none`
			this.setTop(this.findSelectableIndex(index))
		},
		init() {
			this.jumpTo(this.defaultIndex)
			const halfBox = (this.boxHeight - this.itemHeight) / 2
			this.bottom = halfBox + this.itemHeight
			this.top = halfBox - this.count * this.itemHeight
		},
		// 根据index 设置滚动位置
		setTop(index = 0) {
			const { boxHeight, itemHeight } = this
			this.startTop = (boxHeight - itemHeight) / 2 - index * itemHeight
			this.ulStyle.transform = `translate3d(0px, ${this.startTop}px, 0px)`
			this.selectIndex = index
			this.change()
		},
		handleStart(e) {
			this.distStartTop = getClient(e).y
			this.touchStartTime = Date.now()
			// ----
			this.startY = getClient(e).y
			this.momentumTop = this.startTop

			this.ulStyle.transitionDuration = `0ms`
			this.ulStyle.transitionProperty = `none`
			if (IS_PC) {
				document.addEventListener(MOVE_EVENT, this.handleMove, false)
				document.addEventListener(END_EVENT, this.handleEnd, false)
			}
		},
		handleMove(e) {
			e.preventDefault()
			e.stopPropagation()
			this.disY = getClient(e).y - this.startY
			this.startY = getClient(e).y
			if (this.startTop >= this.bottom) {
				this.startTop = this.bottom
			} else if (this.startTop <= this.top) {
				this.startTop = this.top
			} else {
				this.startTop += this.disY
			}
			this.ulStyle.transform = `translate3d(0px, ${this.startTop}px, 0px)`
			const now = Date.now()

			if (now - this.touchStartTime > LIMIT_TIME) {
				this.touchStartTime = now
				this.momentumTop = this.startTop
			}
		},
		handleEnd() {
			if (IS_PC) {
				document.removeEventListener(MOVE_EVENT, this.handleMove, false)
				document.removeEventListener(END_EVENT, this.handleEnd, false)
			}
			const distance = this.startTop - this.momentumTop
			const duration = Date.now() - this.touchStartTime
			const allowMomentum =
				duration < LIMIT_TIME && Math.abs(distance) > LIMIT_DISTANCE
			if (allowMomentum) {
				this.toMove(distance, duration)
			} else {
				this.setTranfromTop()
			}
		},
		setTranfromTop() {
			this.ulStyle.transitionProperty = `all`
			this.ulStyle.transitionDuration = `${DEFAULT_DURATION}ms`
			if (this.startTop >= this.bottom - this.itemHeight) {
				this.setTop(this.findSelectableIndex(0, 1))
			} else if (this.startTop <= this.top + this.itemHeight) {
				this.setTop(this.findSelectableIndex(this.count - 1, -1))
			} else {
				let visualIndex = Math.round(this.startTop / this.itemHeight)
				this.startTop = visualIndex * this.itemHeight
				if (this.startTop > this.bottom) {
					this.startTop = this.bottom - this.itemHeight
					visualIndex = -this.getRoNumber
				} else if (this.startTop < this.top) {
					this.startTop = this.top + this.itemHeight
					visualIndex = this.count + 1
				}
				const rawColumnIndex = this.getRoNumber - visualIndex
				const direction =
					rawColumnIndex > this.selectIndex
						? 1
						: rawColumnIndex < this.selectIndex
							? -1
							: 0
				const targetIndex = this.findSelectableIndex(rawColumnIndex, direction)
				const clampedRawIndex = Math.min(
					Math.max(rawColumnIndex, 0),
					this.count - 1,
				)
				const needSnap =
					this.column[clampedRawIndex]?.disabled ||
					this.selectIndex !== targetIndex
				if (needSnap) {
					this.setTop(targetIndex)
				} else {
					this.ulStyle.transform = `translate3d(0px, ${this.startTop}px, 0px)`
				}
			}
		},
		toMove(distance, duration) {
			const speed = Math.abs(distance / duration)
			const momentumDirection = distance > 0 ? -1 : distance < 0 ? 1 : 0
			distance = this.startTop + (speed / 0.002) * (distance < 0 ? -1 : 1)
			this.ulStyle.transitionProperty = `all`
			this.ulStyle.transitionDuration = `1000ms`
			this.setTop(
				this.findSelectableIndex(
					Math.min(
						Math.max(Math.round(-distance / this.itemHeight), 0),
						this.count - 1,
					),
					momentumDirection,
				),
			)
		},
		change() {
			const item = this.column[this.selectIndex]
			this.$emit('change', item?.disabled ? null : item)
		},
		mousewheel(e) {
			e.preventDefault()
			e.stopPropagation()
			this.ulStyle.transitionDuration = `0ms`
			this.ulStyle.transitionProperty = `none`
			const { deltaX, deltaY } = e
			if (Math.abs(deltaX) < Math.abs(deltaY)) {
				this.startTop = this.startTop - deltaY
				let b = this.bottom - this.itemHeight
				let t = this.top + this.itemHeight
				let shouldMove = true
				if (this.startTop > b) {
					this.startTop = b
					shouldMove = false
				} else if (this.startTop < t) {
					this.startTop = t
					shouldMove = false
				}
				this.ulStyle.transform = `translate3d(0px, ${this.startTop}px, 0px)`
				if (shouldMove) {
					clearInterval(this.wheelTimer)
					this.wheelTimer = setTimeout(this.setTranfromTop, 100)
				}
			}
		},
	},
	mounted() {
		this.init()
		// 监听开始事件
		this.$el.addEventListener(START_EVENT, this.handleStart, false)
		if (IS_PC) {
			this.$el.addEventListener('wheel', this.mousewheel, false)
		} else {
			this.$el.addEventListener(MOVE_EVENT, this.handleMove, false)
			this.$el.addEventListener(END_EVENT, this.handleEnd, false)
		}
	},
	watch: {
		column() {
			this.init()
		},
		defaultIndex() {
			this.jumpTo(this.defaultIndex)
		},
	},
	beforeDestroy() {
		this.$el.removeEventListener(START_EVENT, this.handleStart, false)
		if (IS_PC) {
			this.$el.removeEventListener('wheel', this.mousewheel, false)
			this.$el.removeEventListener(MOVE_EVENT, this.handleMove, false)
			this.$el.removeEventListener(END_EVENT, this.handleEnd, false)
		}
	},
}
</script>

<style lang="sass" scoped>
.list
  margin: 0
  padding: 0
  flex: 1
  width: 100%
  height: 100%
  overflow: hidden

  ul
    margin: 0
    padding: 0
    transition-timing-function: cubic-bezier(0.23, 1, 0.68, 1)
    line-height: 44px

  li
    margin: 0
    padding: 0
    overflow: hidden
    white-space: nowrap
    text-overflow: ellipsis
    padding: 0 5px
    color: #000

    &.disabled
      color: #999
</style>
